/* inno-loader.js — transition loader between the shop lattice and a product page.
   Visual = a projected 3D particle field (a la the homescreen): a wavy plane of dark
   dots receding into depth over a white ground, near dots big/bright, far dots small/
   faint. Shown the instant a card is clicked, it spans the navigation and holds a
   minimum beat on the product page (even on a fast load). The dots FADE IN on arrival
   and FADE OUT on reveal; the piece's vendor sits bottom-left. Self-contained. */
(function () {
  'use strict';

  var BONE = '#E8E4DC', VOID = '#0a0a0a';
  /* The cover stays — it is how this site changes pages — but it now spends about half as long
     doing it. Every number here was set to let the readout be read; the decode below was shortened
     to match, so the title still resolves and is legible, it just doesn't linger. */
  var MIN_MS = 520;    // hold ≥ the decode + a beat, so the title is legible before it fades
  var NAV_DELAY = 250; // let the leaving page + loader cross-fade before we navigate
  var FADE_IN = 300;   // dots fade-in
  var FADE_OUT = 360;  // dots fade-out on reveal
  var CLEAR_HOLD = 110;  // beat held on the CLEAN bone screen after the dots dissolve, before the page appears
  var COVER_FADE = 300;  // must match the #inno-loader opacity transition below

  // ---- 3D field params ----  a camera-facing dot sheet displaced in Z by a rolling
  // wave (+ a radial ripple), so crests push toward you: fills the screen with depth.
  var GX = 108, GY = 82;     // grid density
  var SPX = 15, SPY = 12;    // world spread — taller Y so the field fills the whole screen top-to-bottom
  var Z0 = 6.0, AMP = 1.2, TILT = 0.25;

  /* ---- gyro ----
     The cover answers the phone's tilt the way the homescreen does, only harder: it is on screen
     for about a second, so a lean has to read at a glance. The field's camera travels with the
     tilt (near dots swing further than far ones, which is what makes the depth visible) and the
     sheet turns toward it; the three lines of the readout rake the other way, each at its own
     depth, the same counter-move as the homescreen's corner readout.
     It listens to the orientation the homepage already asked permission for — the loader never
     asks on its own, so without a sensor or a grant nothing here moves. */
  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var SWING = REDUCE ? 0.45 : 0.9;     // camera travel at full tilt (world units)
  var YAW = REDUCE ? 0.06 : 0.13;      // how far the sheet turns toward the tilt
  var PITCH = REDUCE ? 0.05 : 0.1;
  var TEXT_PX = REDUCE ? 11 : 24;      // deepest line's travel at full tilt (the homescreen's is 15)
  var TEXT_DEPTH = [0.55, 0.8, 0.95, 1.05];  // LOADING, the title, the sub-line, the count
  var tiltTX = 0, tiltTY = 0, tiltX = 0, tiltY = 0, tiltAt = 0, tiltWX = 9, tiltWY = 9;
  /* CENTRED ON HOW THE PHONE IS ACTUALLY HELD, not on dead flat — the same origin the
     homescreen learns, and for a sharper reason here. Roll was measured against 0° and pitch
     against a guessed 45°, so a phone held at any ordinary reading angle reported a standing
     deflection from the first event onward. On the homescreen that only means the field rests
     slightly off its orbit; on the cover, rakeText() turns a standing deflection into a standing
     TRANSLATION, so every line of the readout sat a few pixels to one side for the whole of the
     cover's life and had nothing to ease back towards — the lean was leaning away from a
     resting position that was itself off-centre. A couple of degrees of roll is what one hand
     does without noticing, and TEXT_PX has since been raised to 24, so what used to be a pixel
     or two is now most of a character's width.
     The first few readings set the origin instead, averaged, because a single sample taken
     mid-movement would enshrine a wobble as "how it is being held". The first reading alone
     makes baseG exactly rawG, so the cover opens dead centre and leans only when the phone
     actually moves. */
  var baseG = 0, baseB = 45, calibN = 0;
  /* The deadzone fades in rather than switching on. A flat "under 0.04 is zero" puts a step
     right where a phone is usually held, so the readout twitches on and off it; reaching the
     edge of the zone with no value and no slope means the rake grows out of nothing instead. */
  var DEAD = 0.06;
  function shape(v) {
    var a = Math.abs(v);
    if (a <= DEAD) return 0;
    var t = Math.min(1, (a - DEAD) / (1 - DEAD));
    return (v < 0 ? -1 : 1) * t * t * (3 - 2 * t);
  }
  window.addEventListener('deviceorientation', function (e) {
    if (e.gamma == null && e.beta == null) return;
    // `|| 45` on beta read a genuine 0° as the resting angle; a null check is what separates
    // "flat on its face" from "no reading at all"
    var rawG = e.gamma || 0, rawB = (e.beta == null ? 45 : e.beta);
    if (calibN < 6) {
      calibN++;
      baseG += (rawG - baseG) / calibN;
      baseB += (rawB - baseB) / calibN;
    }
    // the homescreen's mapping: ~20 degrees OF LEAN for full deflection, through the same
    // shaping curve — the cover and the field are one gesture and must not feel like two
    tiltTX = shape((rawG - baseG) / 20);
    tiltTY = shape((rawB - baseB) / 20);
  });
  // the lean carries across a navigation, like the decode does, so the next page's cover picks
  // up where this one's was instead of swinging back to centre
  function saveTilt() {
    try { sessionStorage.setItem('inno_load_tilt', tiltX.toFixed(3) + ',' + tiltY.toFixed(3)); } catch (e) {}
  }
  function carryTilt() {
    var v = '';
    try { v = sessionStorage.getItem('inno_load_tilt') || ''; sessionStorage.removeItem('inno_load_tilt'); } catch (e) {}
    var parts = v.split(',');
    if (parts.length === 2) { tiltX = parseFloat(parts[0]) || 0; tiltY = parseFloat(parts[1]) || 0; }
  }
  function easeTilt(t) {
    var dt = tiltAt ? Math.min(50, Math.max(0, t - tiltAt)) : 16;
    tiltAt = t;
    // slow enough to pour rather than snap, quick enough to still arrive inside a short cover
    var k = 1 - Math.exp(-dt / 115);
    tiltX += (tiltTX - tiltX) * k;
    tiltY += (tiltTY - tiltY) * k;
  }
  function rakeText() {
    if (Math.abs(tiltX - tiltWX) < 0.003 && Math.abs(tiltY - tiltWY) < 0.003) return;
    tiltWX = tiltX; tiltWY = tiltY;
    var lines = [labelEl, vendorEl, subEl, pctEl];
    for (var i = 0; i < lines.length; i++) {
      if (!lines[i]) continue;
      var d = TEXT_DEPTH[i];
      lines[i].style.transform = 'translate(' + (-tiltX * TEXT_PX * d).toFixed(2) + 'px,' +
        (-tiltY * TEXT_PX * 0.55 * d).toFixed(2) + 'px)';
    }
  }

  function injectCSS() {
    if (document.getElementById('inno-loader-css')) return;
    var s = document.createElement('style');
    s.id = 'inno-loader-css';
    s.textContent =
      '#inno-loader{position:fixed;inset:0;z-index:2147483000;background:' + BONE + ';' +
        'opacity:0;transition:opacity .3s ease;pointer-events:none;}' +
      '#inno-loader.on{opacity:1;pointer-events:auto;}' +
      '#inno-loader canvas{position:absolute;inset:0;width:100%;height:100%;display:block;}' +
      // ONE CENTRED STACK, not three separately-pinned lines. Each line used to be absolutely
      // positioned at 50% with a fixed offset (-34px / +38px), which only holds while the title
      // is a single line. These titles routinely run to two or three — roughly 80px tall, so
      // ±40px from centre — and the label and percentage ended up sitting on top of the words.
      // A flex column centres the group as a unit, so the gaps hold at any title length.
      '#inno-loader .il-stack{position:absolute;left:50%;top:50%;' +
        // An EXPLICIT width, not shrink-to-fit. With `left:50%` and no `right`, the available
        // width is only half the screen, so a shrink-to-fit box caps there and never reaches
        // its 82vw allowance — which is why an 86-character title was breaking over eight lines
        // instead of three. (The title carried this same flaw before it was put in a stack.)
        'transform:translate(-50%,-50%);z-index:2;' +
        'width:min(82vw,560px);' +
        'display:flex;flex-direction:column;align-items:center;gap:20px;}' +
      /* ALL THREE LINES ARE ONE TREATMENT. The label and the count used to be 11px at half
         opacity with wider tracking — chrome bracketing a headline. Now they are set exactly as
         the title is, so the cover reads as three lines of one readout rather than a title with
         captions above and below it. tabular-nums only matters on the count, but it costs
         nothing on the other two and keeps the rule single. */
      /* the sub-line: what the destination actually opens on. SHOP always lands on New
         Arrivals, and saying so on the cover is the difference between a page that loaded and a
         page that loaded SOMETHING. Set a size down from the readout and bracketed, so it reads
         as a tag on the title rather than a fourth headline. */
      /* padding-left == letter-spacing, on every line. Letter-spacing is added AFTER the last
         character as well as between them, so a centred line is centred with that trailing gap
         counted as part of the text: the glyphs themselves end up half a letter-space (here
         ~1.3px) to the LEFT of centre, on every line, always. Padding the same amount back onto
         the left of the box moves the whole content area right by half of it, which lands the
         ink on centre — and unlike text-indent it holds for the second and third line of a title
         that wraps. */
      '#inno-loader .il-sub{text-align:center;font-family:"IBM Plex Mono",ui-monospace,SFMono-Regular,Menlo,monospace;' +
        'font-size:12px;font-weight:500;letter-spacing:.2em;padding-left:.2em;text-transform:uppercase;color:' + VOID + ';' +
        'opacity:0;transition:opacity .28s ease;margin-top:-8px;}' +
      '#inno-loader.on .il-sub{opacity:.62;}' +
      '#inno-loader .il-sub:empty{display:none;}' +
      '#inno-loader .il-label,#inno-loader .il-pct,#inno-loader .il-vendor{text-align:center;' +
        'font-family:"IBM Plex Mono",ui-monospace,SFMono-Regular,Menlo,monospace;' +
        'font-size:19px;font-weight:500;letter-spacing:.14em;padding-left:.14em;text-transform:uppercase;' +
        'color:' + VOID + ';opacity:0;transition:opacity .28s ease;line-height:1.4;' +
        'font-variant-numeric:tabular-nums;}' +
      '#inno-loader.on .il-label,#inno-loader.on .il-pct,#inno-loader.on .il-vendor{opacity:1;}' +
      'body.inno-leaving #shop,body.inno-leaving #archive{opacity:0!important;filter:blur(16px)!important;transition:opacity .26s ease,filter .26s ease!important;}';
    document.head.appendChild(s);
  }

  var el, canvas, ctx, vendorEl, labelEl, subEl, pctEl, raf, running, startT, fadeT0, dpr, outStart;
  // An absolute ceiling on how long this cover may ever sit on screen. Navigation normally
  // takes it away within ~550ms and every arrival path reveals well inside a second, so this
  // only ever fires when something has gone wrong — and a stuck page is the one failure mode
  // worth spending a timer to make impossible.
  var coverFail = 0;
  // Every raise of the cover gets a number. Tearing it down is a chain of timers, and now that
  // the SPA raises a cover for its own transitions, a second raise can easily land while the
  // first one's teardown is still pending — those stale timers would then remove the cover that
  // had just been rebuilt. Each teardown carries the generation it was scheduled for and stands
  // down if a newer one has started.
  var gen = 0;
  var pctShown = 0, pctTimer = 0;

  // How much of the page is actually here. Blended from two real signals — how far the document
  // has parsed, and how many of its images have decoded — because either alone lies: readyState
  // jumps in coarse steps, and an image count means nothing before the markup exists. A
  // monotonic floor keeps it from ever ticking backwards, which is what makes a percentage feel
  // untrustworthy. It is a genuine measure, not a timed animation pretending to be one.
  function realProgress() {
    var rs = document.readyState;
    var dom = rs === 'complete' ? 1 : rs === 'interactive' ? 0.55 : 0.2;
    var imgs = document.images ? [].slice.call(document.images) : [];
    var shown = imgs.filter(function (i) { return i.getClientRects().length > 0; });
    var pool = shown.length ? shown : imgs;
    var done = pool.filter(function (i) { return i.complete && i.naturalWidth > 0; }).length;
    var img = pool.length ? done / pool.length : 1;
    return Math.min(0.99, dom * 0.45 + img * 0.55);
  }
  /* The TARGET is what was measured; pctShown is what the reader sees, and it walks there.

     The two signals behind realProgress() move in coarse steps — readyState alone is 0.2, then
     0.55, then 1 — so painting each reading straight out made the number leap, 10 to 35 in one
     repaint, which reads as broken rather than as loading. Threading every value in between is
     the whole difference between a count and a stutter.

     Proportional, so it decelerates into each new target rather than arriving at a constant
     clip; floored, so it never stalls when the gap is small; and capped at two points per tick,
     so even a jump to 100 is counted rather than cut to. */
  var pctTarget = 0;
  function aimPct(v) { if (v > pctTarget) pctTarget = v; }      // the floor lives on the target now
  function stepPct() {
    var gap = pctTarget - pctShown;
    if (gap <= 0) return;
    pctShown = Math.min(pctTarget, pctShown + Math.min(gap, Math.max(0.0035, Math.min(gap * 0.12, 0.02))));
    if (pctEl) pctEl.textContent = Math.round(pctShown * 100) + '%';
  }
  // How long the count spends writing itself in before it starts counting. The ticker repaints
  // textContent every 90ms, so without this hold it overwrote its own decode on the first tick
  // and the write-in was never seen.
  var PCT_HOLD = 160;
  function startPct(fromLeaving) {
    clearInterval(pctTimer);
    pctShown = 0;
    pctTarget = 0;
    var lastMeasure = 0;
    // On the page being LEFT there is nothing real to measure yet, so it walks a short ramp to
    // ~35% — the honest share of a navigation that is "request sent". The arriving page picks up
    // from there against its own real numbers.
    var t0 = performance.now() + PCT_HOLD;
    // 32ms, not 90: the walk needs enough repaints to land on the values in between. Measuring
    // is the expensive half (realProgress walks every image on the page), so that stays on its
    // own slower clock and only the easing runs at tick rate.
    pctTimer = setInterval(function () {
      var now = performance.now();
      if (now < t0) return;                  // still writing itself in — leave it alone
      if (fromLeaving) {
        // Climbs from the end of its own write-in to the moment the page hands over, so the
        // number is still moving right up to the navigation. 0.35 stays the honest ceiling for
        // "request sent".
        aimPct(Math.min(0.35, ((now - t0) / 390) * 0.35));
      } else if (now - lastMeasure > 180) {
        lastMeasure = now;
        aimPct(realProgress());
      }
      stepPct();
    }, 32);
  }
  function finishPct() {
    clearInterval(pctTimer); pctTimer = 0;
    pctShown = 1;
    pctTarget = 1;
    if (pctEl) pctEl.textContent = '100%';
    // Deliberately a snap, not a count. hide() calls this and then immediately fades the readout
    // out, so a walk to 100 would play behind an opacity already on its way to zero — dead
    // ceremony. The smoothing that matters is on the way UP, where the number is being read.
  }

  // decode-scramble a label into place (glitch-in), echoing the site's text-scramble.
  // DECODE_MS is the readout's single clock: the label, the title and the percentage all run
  // against it, so the three lines resolve as one event instead of three unrelated ones.
  var DECODE_MS = 260;
  function scrambleTo(elm, text, elapsed, dur) {
    if (!elm) return;
    text = String(text || '');
    // the handle lives on the ELEMENT, not in a module-level slot — with a shared slot,
    // decoding a second line silently cancelled the first
    if (elm._scrRAF) cancelAnimationFrame(elm._scrRAF);
    // the scramble IS the loader's signature — always play it (it's brief), only bail on empty text
    if (!text) { elm.textContent = ''; return; }
    var CH = '!<>-_\\/[]{}=+*^?#%&01';
    // TIME-based decode: a left→right wavefront that always finishes in DUR ms, whatever the
    // title length — so long product names resolve fully within the loader's hold (they didn't before).
    // `elapsed` back-dates the start by however long the SAME decode already ran on the page we left,
    // so it CONTINUES seamlessly (or arrives already-resolved) instead of restarting from scratch —
    // that restart was the visible seam between pages.
    // It has to finish INSIDE the cover's stay. Navigation fires at NAV_DELAY + 130 = 380ms, so
    // the decode is set well under that: any longer and the label reads as random characters for
    // its whole life on the outgoing page. At 260 it resolves, is legible for a beat, and (via
    // `elapsed`) arrives already-resolved on the destination.
    // the count is two or three characters — the title's full window would have it crawling
    var DUR = dur || DECODE_MS, n = text.length, start = performance.now() - (elapsed || 0);
    (function tick(now) {
      now = now || performance.now();
      var p = Math.min(1, (now - start) / DUR);            // overall progress 0..1
      var out = '';
      for (var j = 0; j < n; j++) {
        var c = text.charAt(j);
        if (c === ' ') { out += ' '; continue; }
        var lockAt = (j / n) * 0.8 + 0.08;                 // char j locks as the wavefront passes it
        out += p >= lockAt ? c : CH.charAt(Math.floor(Math.random() * CH.length));
      }
      elm.textContent = out;
      if (p < 1) elm._scrRAF = requestAnimationFrame(tick);
      else { elm.textContent = text; elm._scrRAF = 0; }
    })(performance.now());
  }

  function build() {
    if (el) return el;
    injectCSS();
    el = document.createElement('div');
    el.id = 'inno-loader';
    el.setAttribute('aria-hidden', 'true');
    canvas = document.createElement('canvas');
    vendorEl = document.createElement('div');
    vendorEl.className = 'il-vendor';
    labelEl = document.createElement('div');
    labelEl.className = 'il-label';
    labelEl.textContent = '';
    subEl = document.createElement('div');
    subEl.className = 'il-sub';
    pctEl = document.createElement('div');
    pctEl.className = 'il-pct';
    pctEl.textContent = '0%';
    var stack = document.createElement('div');
    stack.className = 'il-stack';
    stack.appendChild(labelEl);     // LOADING
    stack.appendChild(vendorEl);    // the destination's own title
    stack.appendChild(subEl);       // what it opens on (the shop's sort), when there is one
    stack.appendChild(pctEl);       // the count, under it
    el.appendChild(canvas);
    el.appendChild(stack);
    (document.body || document.documentElement).appendChild(el);
    ctx = canvas.getContext('2d');
    resize();
    window.addEventListener('resize', resize);
    return el;
  }

  function resize() {
    if (!canvas) return;
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
  }

  /* A cooler, interference-y surface whose crests travel to the RIGHT (−tt on x terms),
     warped by y so the streaks read diagonal rather than flat.

     PACE: the same shape as the homescreen's field, and now something like its temper. These
     coefficients were roughly three times higher, which put a crest across the screen in about
     three seconds — a churn, where the homescreen's mesh takes half a minute to do the same. A
     cover that flashes past is not the place for the busiest water on the site. */
  function surf(x, y, tt) {
    return 0.95 * Math.sin(x * 0.5 - tt * 0.8 + Math.sin(y * 0.42 + tt * 0.1) * 1.3)
         + 0.5  * Math.cos(y * 0.5 - tt * 0.32)
         + 0.45 * Math.sin((x - y) * 0.34 - tt * 0.6);   // diagonal streak flowing right
  }

  var drawBuckets = [];
  // project a camera-facing sheet displaced in Z → a full-screen field with depth
  function draw(t) {
    if (!running || !ctx) return;
    var w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    var tt = t * 0.001;

    var fadeIn = Math.min(1, (t - fadeT0) / FADE_IN);   // fadeT0 is back-dated on arrival → field doesn't re-fade-in across a nav
    var fadeOut = outStart ? Math.max(0, 1 - (t - outStart) / FADE_OUT) : 1;
    var vis = fadeIn * fadeOut;
    // NOTE: the bone ground deliberately stays OPAQUE while the dots dissolve, so the mesh
    // clears to a full clean screen before the page is revealed (no page showing through mid-fade)

    var CX = w / 2, CY = h / 2, FOCAL = h;
    ctx.fillStyle = VOID;
    var buckets = drawBuckets;
    easeTilt(t);
    rakeText();
    var camX = tiltX * SWING, camY = tiltY * SWING * 0.7;
    var yaw = tiltX * YAW, pitch = tiltY * PITCH;
    // the whole field drifts to the RIGHT (and accelerates as it clears on exit). The drift was
    // stacked on top of the wave's own travel, so the two compounded into a scroll; it is now a
    // slow current under a slow surface, and the exit surge is scaled to match.
    var drift = tt * 0.6 + (outStart ? (1 - fadeOut) * 2.5 : 0);

    for (var iy = 0; iy < GY; iy++) {
      var by = (iy / GY - 0.5) * SPY;
      for (var ix = 0; ix < GX; ix++) {
        var bx = (ix / GX - 0.5) * SPX + drift;
        bx = ((bx + SPX / 2) % SPX + SPX) % SPX - SPX / 2;   // wrap → continuous rightward scroll
        // wave pushes crests forward; the tilt turns the whole sheet toward the lean
        var z = Z0 + surf(bx, by, tt) * AMP + by * TILT + bx * yaw + by * pitch;
        if (z < 0.6) continue;
        var s = FOCAL / z;
        var px = CX + (bx - camX) * s;
        var py = CY + (by - camY) * s;
        if (px < -30 || px > w + 30 || py < -30 || py > h + 30) continue;
        var rad = s * 0.010 * dpr;
        if (rad < 0.4 * dpr) rad = 0.4 * dpr;
        var prox = (Z0 + 2.0 - z) / 4.0;                   // 0 far → 1 near
        prox = prox < 0 ? 0 : prox > 1 ? 1 : prox;
        /* Bucketed, not drawn here. Every dot used to set globalAlpha and run its own
           beginPath/arc/fill — ~8,800 state changes and path rasterisations per frame, which put
           the cover at about 30fps while the next page was parsing. Same dots, same alphas (16
           steps, below what the eye can separate at these opacities), 16 fills instead. */
        var a = Math.min(1, vis * (0.12 + 0.55 * prox));
        var bi = a * 16 | 0; if (bi > 15) bi = 15;
        var bucket = buckets[bi] || (buckets[bi] = []);
        bucket.push(px, py, rad);
      }
    }
    for (var bk = 0; bk < 16; bk++) {
      var list = buckets[bk];
      if (!list || !list.length) continue;
      ctx.globalAlpha = (bk + 0.5) / 16;
      ctx.beginPath();
      for (var q = 0; q < list.length; q += 3) {
        ctx.moveTo(list[q] + list[q + 2], list[q + 1]);
        ctx.arc(list[q], list[q + 1], list[q + 2], 0, 6.2832);
      }
      ctx.fill();
      list.length = 0;
    }
    ctx.globalAlpha = 1;
    if (outStart && fadeOut <= 0) { running = false; return; }   // dots gone → stop drawing, KEEP the clean cover
    raf = requestAnimationFrame(draw);
  }

  function show(vendor, elapsed, sub) {
    build();
    gen++;                         // anything still counting down for an older cover is void now
    running = true;
    outStart = 0;
    startT = performance.now();                          // fresh — drives this page's own MIN_MS hold beat
    fadeT0 = performance.now() - (elapsed || 0);         // back-dated on arrival so the field fade-in doesn't replay
    if (elapsed) carryTilt();
    tiltAt = 0; tiltWX = 9;                                // re-ease from wherever the lean is, and repaint the text
    clearTimeout(coverFail);
    coverFail = setTimeout(function () {
      if (!el) return;
      document.body.classList.remove('inno-leaving');
      cleanup();
    }, 9000);
    if (labelEl) scrambleTo(labelEl, 'LOADING', elapsed);        // same clock as the title
    // the count writes itself in too, so all three lines arrive the same way; once it has
    // resolved, startPct takes over and it just counts
    if (pctEl) scrambleTo(pctEl, '0%', elapsed, PCT_HOLD);
    if (vendorEl) scrambleTo(vendorEl, vendor || '', elapsed);   // the label decode CONTINUES across the nav (no restart)
    if (subEl) { if (sub) scrambleTo(subEl, '[ ' + sub + ' ]', elapsed); else subEl.textContent = ''; }
    startPct(!elapsed && !document.body.classList.contains('template-product'));
    if (el) el.style.opacity = '';
    void el.offsetWidth;          // reflow so the opacity transition plays
    el.classList.add('on');
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(draw);
  }

  function cleanup() {
    clearTimeout(coverFail); coverFail = 0;
    running = false;
    clearInterval(pctTimer); pctTimer = 0;      // the element is about to go; stop writing to it
    cancelAnimationFrame(raf);
    if (el && el.parentNode) el.parentNode.removeChild(el);
    el = null; labelEl = null; pctEl = null; subEl = null; outStart = 0;
  }

  // Reveal in three distinct beats, so every page transition reads the same way:
  //   1. the MESH dissolves while the bone ground stays fully opaque  → a clean, empty screen
  //   2. a short hold on that clean screen (CLEAR_HOLD)
  //   3. the page's media + related fade in underneath as the cover cross-fades away
  function reveal() {
    if (outStart || document.body.classList.contains('pdp-ready')) return;   // once
    outStart = performance.now();
    finishPct();                     // it is genuinely done — say so before anything fades
    // ALL THREE lines go together. Only the title used to be faded here; the label and the
    // percentage were still riding the `.on` class, which isn't removed until FADE_OUT +
    // CLEAR_HOLD later — so they sat on an otherwise empty screen after the title had gone.
    if (labelEl) labelEl.style.opacity = '0';
    if (pctEl) pctEl.style.opacity = '0';
    if (subEl) subEl.style.opacity = '0';
    // The label simply GOES. It used to glide down onto the destination's heading box, which
    // read as the text sliding into the page rather than the page arriving — so the title now
    // dissolves here and comes back on its own as part of the page's staged entrance.
    if (vendorEl) vendorEl.style.opacity = '0';
    if (running) { cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); }
    var mineR = gen;
    setTimeout(function () {
      if (mineR !== gen) return;                     // superseded by a newer cover
      // on a clean full-screen bone now — start the page's own fade-in cascade and lift the cover
      document.body.classList.add('pdp-ready');
      if (el) el.classList.remove('on');
      setTimeout(function () { if (mineR === gen) cleanup(); }, COVER_FADE + 80);
      // FAILSAFE. The staged entrance is a set of CSS animations with `backwards` fill, so
      // every element sits at opacity 0 until its animation actually runs. If it doesn't —
      // throttled/hidden tab, a compositor stall, animations disabled — the whole product page
      // stays invisible, which is far worse than a missing flourish. Past the last stage we
      // pin everything visible; if the animations did run this changes nothing.
      setTimeout(function () { document.body.classList.add('pdp-settled'); }, 2600);
    }, FADE_OUT + CLEAR_HOLD);
  }

  function pdpLabel() {
    var t = '';
    try { t = sessionStorage.getItem('inno_title') || ''; } catch (e) {}
    if (!t) { var n = document.querySelector('.inno-main-t'); t = n ? n.textContent.trim() : ''; }
    // never fall back to the vendor ("Innocences") — that read as a stray watermark on the loader
    try { sessionStorage.removeItem('inno_title'); sessionStorage.removeItem('inno_vendor'); } catch (e) {}
    return t;
  }

  // how long the loader was ALREADY running on the page we just left (wall-clock ms), so this page
  // can CONTINUE the decode/field instead of restarting it. One-shot — cleared the moment it's read.
  function carryElapsed() {
    var t0 = 0;
    try { t0 = parseInt(sessionStorage.getItem('inno_load_t0') || '0', 10); sessionStorage.removeItem('inno_load_t0'); } catch (e) {}
    if (!t0) return 0;
    var e = Date.now() - t0;
    return (e > 0 && e < 6000) ? e : 0;                 // ignore stale / clock-skew values → fresh decode
  }

  // product page: cover immediately, hold ≥ MIN_MS past load, then fade + reveal
  function runPdp() {
    show(pdpLabel(), carryElapsed());
    function done() {
      setTimeout(reveal, Math.max(0, MIN_MS - (performance.now() - startT)));
    }
    if (document.readyState === 'complete') done();
    else window.addEventListener('load', done);
    setTimeout(function () { if (!document.body.classList.contains('pdp-ready')) reveal(); }, 5000);
  }

  // shop lattice: a card click shows the loader (with that piece's vendor), then navigates
  function bindCards() {
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a.pcell');
      if (!a || (e.target.closest && e.target.closest('.p-cyc'))) return;   // cycler ≠ navigation
      var href = a.getAttribute('href');
      if (!href || href === '#' || href.indexOf('/products/') === -1) return;
      e.preventDefault();
      // prefer the full product title — the caption's .p-t has the vendor stripped out, so
      // using it meant the loader decoded one string and the product page displayed another
      var title = a.getAttribute('data-full-title') || (a.querySelector('.p-t') || {}).textContent || '';
      var vend = (a.querySelector('.p-v') || {}).textContent || '';
      try { sessionStorage.setItem('inno_title', title); sessionStorage.setItem('inno_vendor', vend); } catch (err) {}
      document.body.classList.add('inno-leaving');   // cards snap-fade + blur out first
      setTimeout(function () {
        try { sessionStorage.setItem('inno_load_t0', String(Date.now())); } catch (er) {}   // mark decode start → product page continues it
        show(title);                                   // the piece's title decodes in on the loader (never the vendor)
      }, 170);
      setTimeout(function () { saveTilt(); window.location.href = href; }, NAV_DELAY + 130);
    }, false);
  }

  // where a product-page nav link leads → the label the loader decodes in on the way out
  function destLabel(href, fallback) {
    if (/[?&]shop=1/.test(href)) return 'SHOP';
    if (/open=(visit|archive)/.test(href)) return 'VISIT';
    if (/open=about/.test(href)) return 'INDEX';
    if (href === '/' || href === '/?' || href === '') return 'INNOCENCES';
    return (fallback || '').trim().toUpperCase();
  }

  // product page → the header logo/nav navigate back to the SPA (shop / archive / index).
  // Play the same loading animation on the way out, labelled with the destination.
  function bindExits() {
    if (!document.body.classList.contains('template-product')) return;
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('.inno-header-logo, .inno-header-nav a');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) !== '/') return;   // only in-site navigations
      if (/cart=1/.test(href)) return;                // cart opens the drawer in place — not a navigation
      e.preventDefault();
      document.body.classList.add('inno-leaving');
      try { sessionStorage.setItem('inno_load_t0', String(Date.now())); } catch (er) {}   // mark decode start → destination page continues it
      show(destLabel(href, a.textContent));
      setTimeout(function () { saveTilt(); window.location.href = href; }, NAV_DELAY + 130);
    }, false);
  }

  // homepage arrival FROM a cross-page nav (?shop / ?open) → the SPA loads the home field first,
  // then transitions to the destination, so you'd glimpse the homepage mid-jump. Cover the whole
  // arrival with the loader (continuous with the leaving page's loader) and reveal once it settles.
  function runHomeEntry() {
    var boot = document.getElementById('inno-boot'), booted = true;
    try { booted = !!sessionStorage.getItem('inno_booted'); } catch (e) {}
    if (boot && !booted) return false;                 // first-visit boot covers the arrival itself
    var p = new URLSearchParams(location.search);
    var dest = p.get('open') || (p.get('shop') === '1' ? 'shop' : '');
    if (dest !== 'shop' && dest !== 'visit' && dest !== 'archive' && dest !== 'about') return false;
    var LABEL = { shop: 'SHOP', visit: 'VISIT', archive: 'VISIT', about: 'INDEX' };
    show(LABEL[dest] || dest.toUpperCase(), carryElapsed(), dest === 'shop' ? 'New arrivals' : '');   // continue the decode the leaving page started
    var pre = document.getElementById('inno-preload');   // hand off the pre-paint cover to the loader
    if (pre && pre.parentNode) pre.parentNode.removeChild(pre);
    var done = false;
    function tryReveal() {
      if (done || !document.body.classList.contains('page-settled')) return;
      done = true; obs.disconnect(); reveal();          // destination is live under the cover → lift it
    }
    var obs = new MutationObserver(tryReveal);
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    setTimeout(function () { if (!done) { done = true; obs.disconnect(); reveal(); } }, 3500);  // safety
    return true;
  }

  // BACK/FORWARD RESTORE. The bfcache resurrects a page exactly as it was frozen — and it is
  // frozen mid-navigation, with `inno-leaving` on the body and this loader covering the screen.
  // No script re-runs on a restore, so none of the reveal paths below ever fire and nothing
  // takes the cover down: the page comes back as a loading screen that never ends. Every
  // existing safety timer is armed during a FRESH load, so none of them help here either.
  // Safari caches far more eagerly than Chrome, which is why this shows up on iOS first.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;                   // a normal load already runs init()
    document.body.classList.remove('inno-leaving');
    cleanup();                                  // removes #inno-loader outright
    var pre = document.getElementById('inno-preload');
    if (pre && pre.parentNode) pre.parentNode.removeChild(pre);
    // a restored product page kept its staged entrance state; make sure it is fully revealed
    if (document.body.classList.contains('template-product')) {
      document.body.classList.add('pdp-ready');
      document.body.classList.add('pdp-settled');
    }
  });

  function init() {
    if (document.body.classList.contains('template-product')) runPdp();
    else if (document.body.classList.contains('template-index')) runHomeEntry();
    bindCards();
    bindExits();
  }
  if (document.body) init();
  else document.addEventListener('DOMContentLoaded', init);

  // show(vendor) for programmatic use; _frame(t) renders a single frame (test hook)
  // Take the cover down from outside. reveal() is product-page specific (it also flips the
  // pdp-* classes), so the SPA gets its own exit that only dissolves the cover.
  function hide() {
    if (!el || outStart) return;
    outStart = performance.now();
    finishPct();
    if (labelEl) labelEl.style.opacity = '0';
    if (vendorEl) vendorEl.style.opacity = '0';
    if (pctEl) pctEl.style.opacity = '0';
    if (subEl) subEl.style.opacity = '0';
    if (running) { cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); }
    var mine = gen;
    setTimeout(function () {
      if (mine !== gen) return;                      // a newer cover is up — leave it alone
      if (el) el.classList.remove('on');
      setTimeout(function () { if (mine === gen) cleanup(); }, COVER_FADE + 80);
    }, FADE_OUT + CLEAR_HOLD);
  }
  window.innoLoader = { show: show, hide: hide, _frame: function (t) { if (!el) { show(''); } running = true; draw(t || performance.now()); },
    // judge the lean without a phone: sets the sensor reading and snaps to it
    _tilt: function (x, y) { tiltTX = tiltX = x || 0; tiltTY = tiltY = y || 0; tiltWX = 9; rakeText(); return [labelEl, vendorEl, pctEl].map(function (l) { return l ? l.style.transform : null; }); } };
})();
